import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, Sparkles } from 'lucide-react';

export const AboutPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen overflow-hidden bg-brand-black text-white">
      <section className="relative flex min-h-[100svh] items-center overflow-hidden pb-24 pt-32 md:pb-28 md:pt-40">
        <div className="absolute inset-y-0 right-0 w-full lg:w-[58%]">
          <img
            src="/images/about_us.jpg"
            alt={t('aboutPage.company.imageAlt')}
            className="h-full w-full object-cover object-[58%_center] saturate-[0.72] contrast-[1.06] brightness-[0.66]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-black via-brand-black/75 to-brand-black/10 lg:from-brand-black lg:via-brand-black/45 lg:to-black/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-transparent to-black/35" />
        </div>

        <div className="pointer-events-none absolute -left-24 top-1/2 h-[32rem] w-[32rem] -translate-y-1/2 rounded-full bg-brand-gold/[0.08] blur-[130px]" />
        <div className="pointer-events-none absolute right-[-4%] top-[20%] hidden font-display text-[13rem] font-bold leading-none text-white/[0.035] xl:block">
          HOME
        </div>

        <div className="container relative z-10 mx-auto max-w-7xl px-6 md:px-12">
          <div className="max-w-5xl">
            <div className="mb-8 flex items-center gap-4">
              <span className="h-px w-12 bg-brand-gold" />
              <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-brand-gold md:text-xs">
                {t('aboutPage.eyebrow')}
              </p>
            </div>

            <h1 className="font-display text-[2.4rem] font-bold leading-[0.94] tracking-[-0.055em] sm:text-6xl md:text-[clamp(4.5rem,7.2vw,7.2rem)] md:leading-[0.92]">
              <span className="block">{t('aboutPage.heroLine1')}</span>
              <span className="mt-3 block text-brand-gold md:mt-5">
                {t('aboutPage.heroLine2')}
              </span>
            </h1>

            <p className="mt-9 max-w-lg text-sm leading-7 text-white/62 md:mt-12 md:text-base md:leading-8">
              {t('aboutPage.heroLead')}
            </p>
          </div>
        </div>

        <div className="absolute bottom-7 left-6 z-10 flex items-center gap-3 text-[10px] uppercase tracking-[0.18em] text-white/40 md:bottom-10 md:left-12">
          <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15">
            <ArrowDown className="h-4 w-4 text-brand-gold" />
          </span>
          {t('aboutPage.scroll')}
        </div>

      </section>

      <section className="relative py-24 md:py-36 lg:py-44">
        <div className="pointer-events-none absolute left-1/2 top-0 h-px w-[82%] -translate-x-1/2 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        <div className="container mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid gap-16 lg:grid-cols-[minmax(220px,0.75fr)_minmax(0,2.15fr)] lg:gap-28">
            <div>
              <p className="font-display text-xs tracking-[0.22em] text-brand-gold">{t('aboutPage.company.label')}</p>
              <p className="mt-6 max-w-xs text-sm leading-7 text-white/40">
                {t('aboutPage.company.name')}
              </p>
            </div>

            <div>
              <p className="max-w-4xl font-display text-2xl font-semibold leading-[1.5] tracking-[-0.02em] text-white/90 md:text-4xl md:leading-[1.42]">
                {t('aboutPage.company.p1')}
              </p>

              <div className="mt-14 grid gap-10 md:mt-20 md:grid-cols-2 md:gap-14">
                <article className="relative pl-7">
                  <span className="absolute left-0 top-2 h-2 w-2 rounded-full bg-brand-gold" />
                  <p className="text-sm leading-8 text-white/52 md:text-base md:leading-9">
                    {t('aboutPage.company.p2')}
                  </p>
                </article>
                <article className="relative pl-7">
                  <span className="absolute left-0 top-2 h-2 w-2 rounded-full border border-brand-gold" />
                  <p className="text-sm leading-8 text-white/52 md:text-base md:leading-9">
                    {t('aboutPage.company.p3')}
                  </p>
                </article>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#d4a943] text-black">
        <div className="pointer-events-none absolute -left-[3%] top-[-3%] font-display text-[9rem] font-bold leading-none text-black/[0.055] md:text-[17rem]">
          WYNDME
        </div>
        <div className="pointer-events-none absolute bottom-[-12rem] right-[-10rem] h-[34rem] w-[34rem] rounded-full border border-black/10" />
        <div className="pointer-events-none absolute bottom-[-8rem] right-[-6rem] h-[24rem] w-[24rem] rounded-full border border-black/10" />

        <div className="container relative mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid min-h-[760px] items-center gap-8 py-20 lg:grid-cols-[1.02fr_0.98fr] lg:gap-16 lg:py-24">
            <div className="relative flex min-h-[420px] items-end justify-center lg:min-h-[600px]">
              <div className="absolute bottom-[8%] left-1/2 h-24 w-[78%] -translate-x-1/2 rounded-[50%] bg-black/20 blur-2xl" />
              <img
                src="/images/wyndme_yellow_refined.png"
                alt={t('aboutPage.brand.imageAlt')}
                className="relative w-full max-w-[560px] object-contain drop-shadow-[0_35px_30px_rgba(0,0,0,0.18)]"
              />
              <div className="absolute bottom-3 left-0 flex items-center gap-3 lg:bottom-7">
                <span className="h-2 w-2 rounded-full bg-black" />
                <span className="font-display text-[10px] font-semibold tracking-[0.2em] text-black/55">
                  O1 PRO / COMPANION ROBOT
                </span>
              </div>
            </div>

            <div className="py-4 lg:py-16">
              <p className="font-display text-xs font-semibold tracking-[0.22em] text-black/48">WYNDME</p>
              <h2 className="mt-6 font-display text-5xl font-bold tracking-[-0.045em] md:text-7xl">
                {t('aboutPage.brand.title')}
              </h2>
              <h3 className="mt-6 max-w-xl text-xl font-semibold leading-snug text-black/72 md:text-3xl">
                {t('aboutPage.brand.subtitle')}
              </h3>

              <p className="mt-10 max-w-xl text-base leading-9 text-black/68 md:text-lg md:leading-10">
                {t('aboutPage.brand.p1')}
              </p>

              <p className="mt-12 max-w-xl border-l-2 border-black pl-6 font-display text-2xl font-semibold leading-relaxed md:text-3xl">
                {t('aboutPage.brand.p4')}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="relative flex min-h-[920px] items-center overflow-hidden py-28 md:py-40">
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[52rem] w-[52rem] -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/[0.045]" />
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[38rem] w-[38rem] -translate-x-1/2 -translate-y-1/2 rounded-full border border-brand-gold/10" />
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[24rem] w-[24rem] -translate-x-1/2 -translate-y-1/2 rounded-full border border-brand-gold/15" />
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[18rem] w-[18rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-gold/[0.08] blur-[80px]" />
        <div className="pointer-events-none absolute left-[calc(50%+18.5rem)] top-1/2 hidden h-3 w-3 -translate-y-1/2 rounded-full bg-brand-gold shadow-[0_0_35px_rgba(245,197,66,.9)] md:block" />

        <div className="container relative mx-auto max-w-6xl px-6 text-center md:px-12">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-gold text-black shadow-[0_0_70px_rgba(212,169,67,.35)]">
            <Sparkles className="h-6 w-6" />
          </span>
          <p className="mt-8 font-display text-xs tracking-[0.22em] text-brand-gold">
            {t('aboutPage.vision.label')}
          </p>
          <h2 className="mx-auto mt-7 max-w-5xl font-display text-[clamp(3rem,6.5vw,6.6rem)] font-bold leading-[0.98] tracking-[-0.055em]">
            {t('aboutPage.vision.title')}
          </h2>

          <p className="mx-auto mt-12 max-w-3xl font-display text-xl font-medium leading-relaxed text-white/82 md:text-3xl">
            {t('aboutPage.vision.p1')}
          </p>
          <p className="mx-auto mt-8 max-w-3xl text-sm leading-8 text-white/50 md:text-base md:leading-9">
            {t('aboutPage.vision.p2')}
          </p>

          <div className="mx-auto mt-14 flex w-fit items-center gap-4 rounded-full border border-white/10 bg-white/[0.035] px-6 py-3">
            <span className="h-2 w-2 rounded-full bg-brand-gold" />
            <span className="font-display text-[10px] tracking-[0.2em] text-white/50">
              HUMAN × AI × HOME
            </span>
          </div>
        </div>
      </section>
    </div>
  );
};
